import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTheme } from "../data/data";
import ThemeSwitcher from "./ThemeSwitcher";
import LanguageSwitcher from "./LanguageSwitcher";
import logoDark from "../assets/logo-dark.png";

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/products", label: "Products" },
  { to: "/quality", label: "Quality" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { theme } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 55);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => setMobileOpen(false), [location]);

  const isActive = (to) => location.pathname === to;

  return (
    <>
      <style>{`
        @keyframes dropIn { from{opacity:0;transform:translateY(-8px)} to{opacity:1;transform:translateY(0)} }
        @keyframes fadeUp { from{opacity:0;transform:translateY(26px)} to{opacity:1;transform:translateY(0)} }
        @keyframes spinRing { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes floatBob { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-16px)} }
        @keyframes slideIn { from{opacity:0;transform:translateX(-20px)} to{opacity:1;transform:translateX(0)} }
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        body { font-family: 'DM Sans', sans-serif; }
        ::-webkit-scrollbar { width: 5px; }
        ::-webkit-scrollbar-thumb { background: ${theme.primary}; border-radius: 4px; }
        ::-webkit-scrollbar-track { background: ${theme.pageBg}; }
        a { color: inherit; }
        img { max-width: 100%; }
        @media (max-width: 900px) {
          .nav-desktop { display: none !important; }
          .nav-mobile-btn { display: flex !important; }
        }
        @media (min-width: 901px) {
          .nav-mobile-menu { display: none !important; }
          .nav-mobile-btn { display: none !important; }
        }
        @media (max-width: 768px) {
          .hero-ring { display: none !important; }
          .grid-2 { grid-template-columns: 1fr !important; }
          .grid-3 { grid-template-columns: 1fr !important; }
          .grid-4 { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>

      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 200,
        background: theme.navBg,
        borderBottom: `1px solid ${scrolled ? theme.border : "transparent"}`,
        boxShadow: scrolled ? `0 2px 20px ${theme.shadow}` : "none",
        backdropFilter: "blur(14px)",
        transition: "all 0.3s ease",
      }}>
        <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 5vw", display: "flex", alignItems: "center", justifyContent: "space-between", height: scrolled ? 62 : 70, transition: "height 0.3s" }}>

          {/* Logo */}
          <Link to="/" style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
            <img
              src={logoDark}
              alt="Kuldiip Enterprises"
              style={{
                height: scrolled ? 42 : 50,
                width: "auto",
                display: "block",
                transition: "height 0.3s",
                flexShrink: 0,
              }}
            />
            <div>
              <div style={{ fontFamily: "'Playfair Display',serif", fontSize: "1.1rem", fontWeight: 700, color: theme.primary, lineHeight: 1 }}>KULDIIP</div>
              <div style={{ fontSize: "0.58rem", letterSpacing: "0.16em", textTransform: "uppercase", color: theme.textMuted, marginTop: 2 }}>Enterprises</div>
            </div>
          </Link>

          {/* Desktop nav */}
          <div className="nav-desktop" style={{ display: "flex", alignItems: "center", gap: "1.6rem" }}>
            {NAV_LINKS.map(l => (
              <Link key={l.to} to={l.to} style={{
                textDecoration: "none", fontSize: "0.8rem", letterSpacing: "0.06em",
                textTransform: "uppercase", fontWeight: isActive(l.to) ? 700 : 500,
                color: isActive(l.to) ? theme.primary : theme.textSecondary,
                borderBottom: isActive(l.to) ? `2px solid ${theme.accent}` : "2px solid transparent",
                paddingBottom: 2, transition: "all 0.22s",
              }}
                onMouseEnter={e => { e.currentTarget.style.color = theme.primary; }}
                onMouseLeave={e => { e.currentTarget.style.color = isActive(l.to) ? theme.primary : theme.textSecondary; }}>
                {l.label}
              </Link>
            ))}
            <ThemeSwitcher />
            <LanguageSwitcher />
            <Link to="/contact" style={{
              background: theme.accent, color: theme.textOnAccent,
              textDecoration: "none", padding: "0.52rem 1.3rem",
              borderRadius: 7, fontSize: "0.78rem", fontWeight: 700,
              letterSpacing: "0.05em", transition: "all 0.22s",
              boxShadow: `0 2px 10px ${theme.accent}40`,
            }}
              onMouseEnter={e => { e.currentTarget.style.background = theme.accentHover; e.currentTarget.style.transform = "translateY(-2px)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = theme.accent; e.currentTarget.style.transform = "translateY(0)"; }}>
              Get Quote
            </Link>
          </div>

          {/* Mobile burger */}
          <button className="nav-mobile-btn" onClick={() => setMobileOpen(!mobileOpen)}
            style={{ display: "none", background: "none", border: `1.5px solid ${theme.border}`, borderRadius: 7, padding: "0.4rem 0.6rem", cursor: "pointer", color: theme.textPrimary, fontSize: "1.1rem" }}>
            {mobileOpen ? "✕" : "☰"}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="nav-mobile-menu" style={{ background: theme.cardBg, borderTop: `1px solid ${theme.border}`, padding: "1rem 5vw 1.5rem" }}>
            {NAV_LINKS.map((l, i) => (
              <Link key={l.to} to={l.to} style={{
                display: "block", padding: "0.8rem 0", textDecoration: "none",
                fontSize: "0.95rem", fontWeight: isActive(l.to) ? 700 : 500,
                color: isActive(l.to) ? theme.primary : theme.textSecondary,
                borderBottom: `1px solid ${theme.border}`,
                animation: `slideIn 0.25s ease ${i * 0.05}s both`,
              }}>{l.label}</Link>
            ))}
            <div style={{ marginTop: "1rem", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <ThemeSwitcher />
              <LanguageSwitcher />
              <Link to="/contact" style={{ background: theme.accent, color: theme.textOnAccent, textDecoration: "none", padding: "0.6rem 1.2rem", borderRadius: 7, fontSize: "0.85rem", fontWeight: 700 }}>Get Quote</Link>
            </div>
          </div>
        )}
      </nav>
    </>
  );
}
